import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const completionTraceKindSchema = z.enum([
    "code_change",
    "pr_merge",
    "deploy",
    "research",
    "ticket_closure",
    "refund",
    "ambiguous",
]);
export const completionProposalCriterionSchema = z
    .object({
    id: protocolIdSchema,
    statement: z.string().trim().min(1),
    requiredOracle: protocolIdSchema,
    absentFromTrace: z.boolean(),
    canBeIndependentOracle: z.boolean(),
    namedGapIfUnavailable: protocolIdSchema,
})
    .strict();
export const completionTemplateSchema = z
    .object({
    id: protocolIdSchema,
    traceKind: completionTraceKindSchema.exclude(["ambiguous"]),
    expectedAbsentChecks: z.array(protocolIdSchema),
    criteria: z.array(completionProposalCriterionSchema).min(1),
})
    .strict()
    .superRefine((value, context) => {
    const criteriaIds = new Set(value.criteria.map((criterion) => criterion.id));
    for (const absentCheck of value.expectedAbsentChecks) {
        if (!criteriaIds.has(absentCheck)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["expectedAbsentChecks"],
                message: `expected absent check ${absentCheck} is missing from template criteria`,
            });
        }
    }
});
export const completionProposalSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    proposalId: protocolIdSchema,
    traceKind: completionTraceKindSchema,
    selectedTemplateId: protocolIdSchema.nullable(),
    state: z.enum(["proposed", "abstain"]),
    evidenceBackedScore: z.number().min(0).max(1),
    rankedByLocalModel: z.boolean(),
    activatedContract: z.literal(false),
    criteria: z.array(completionProposalCriterionSchema),
    namedGaps: z.array(protocolIdSchema),
    sourceTraceDigest: sha256DigestSchema,
    proposalDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.state === "proposed" && value.selectedTemplateId === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["selectedTemplateId"],
            message: "proposed completion requires a selected template",
        });
    }
    if (value.state === "proposed" &&
        value.criteria.some((criterion) => !criterion.canBeIndependentOracle)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["criteria"],
            message: "proposal criteria cannot promote non-independent evidence into an oracle",
        });
    }
});
export const completionProposalTraceEventSchema = z
    .object({
    id: protocolIdSchema,
    kind: z.enum([
        "worker_claim",
        "tool_result",
        "command_oracle",
        "receipt_observed",
        "deploy_observed",
        "ticket_observed",
        "oracle_observed",
    ]),
    text: z.string().trim().min(1),
    digest: sha256DigestSchema,
    permission: z.enum(["content", "redacted", "withheld"]),
    independentOracle: protocolIdSchema.nullable(),
    trusted: z.boolean(),
})
    .strict();
export const completionProposalTraceInputSchema = z
    .object({
    traceText: z.string().trim().min(1),
    observationBoundary: z.array(protocolIdSchema),
    availableOracles: z.array(protocolIdSchema),
    sourceEventDigests: z.record(sha256DigestSchema),
    withheldEventIds: z.array(protocolIdSchema),
    redactedEventIds: z.array(protocolIdSchema),
    untrustedEventIds: z.array(protocolIdSchema),
    traceDigest: sha256DigestSchema,
})
    .strict();
export const DEFAULT_COMPLETION_TEMPLATES = Object.freeze([
    {
        id: "template:code-change",
        traceKind: "code_change",
        expectedAbsentChecks: ["criterion:landed-tree", "criterion:independent-test-receipt"],
        criteria: [
            {
                id: "criterion:landed-tree",
                statement: "The submitted code is bound to the landed repository tree.",
                requiredOracle: "oracle:git-tree",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:landed-tree-unavailable",
            },
            {
                id: "criterion:independent-test-receipt",
                statement: "The relevant checks pass from an independent runner.",
                requiredOracle: "oracle:independent-command",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:independent-test-receipt-unavailable",
            },
        ],
    },
    {
        id: "template:pr-merge",
        traceKind: "pr_merge",
        expectedAbsentChecks: ["criterion:merge-commit", "criterion:required-review"],
        criteria: [
            {
                id: "criterion:merge-commit",
                statement: "The pull request merged into the intended base branch.",
                requiredOracle: "oracle:vcs-merge-state",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:merge-commit-unavailable",
            },
            {
                id: "criterion:required-review",
                statement: "Required review or branch protection was satisfied.",
                requiredOracle: "oracle:vcs-branch-protection",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:required-review-unavailable",
            },
        ],
    },
    {
        id: "template:deploy",
        traceKind: "deploy",
        expectedAbsentChecks: ["criterion:deployed-health", "criterion:external-host-state"],
        criteria: [
            {
                id: "criterion:deployed-health",
                statement: "The deployed service reports healthy after the release.",
                requiredOracle: "oracle:deployment-health",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:deployed-health-unavailable",
            },
            {
                id: "criterion:external-host-state",
                statement: "The hosting provider reports the intended version is live.",
                requiredOracle: "oracle:hosting-provider",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:external-host-state-unavailable",
            },
        ],
    },
    {
        id: "template:research",
        traceKind: "research",
        expectedAbsentChecks: ["criterion:cited-sources", "criterion:claim-coverage"],
        criteria: [
            {
                id: "criterion:cited-sources",
                statement: "Material claims are backed by accessible cited sources.",
                requiredOracle: "oracle:source-audit",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:cited-sources-unavailable",
            },
            {
                id: "criterion:claim-coverage",
                statement: "The answer covers the requested research questions.",
                requiredOracle: "oracle:rubric-review",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:claim-coverage-unavailable",
            },
        ],
    },
    {
        id: "template:ticket-closure",
        traceKind: "ticket_closure",
        expectedAbsentChecks: ["criterion:ticket-state", "criterion:acceptance-criteria"],
        criteria: [
            {
                id: "criterion:ticket-state",
                statement: "The ticket is closed in the system of record.",
                requiredOracle: "oracle:ticket-system",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:ticket-state-unavailable",
            },
            {
                id: "criterion:acceptance-criteria",
                statement: "Named ticket acceptance criteria are satisfied.",
                requiredOracle: "oracle:acceptance-review",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:acceptance-criteria-unavailable",
            },
        ],
    },
    {
        id: "template:refund",
        traceKind: "refund",
        expectedAbsentChecks: ["criterion:payment-record", "criterion:customer-notification"],
        criteria: [
            {
                id: "criterion:payment-record",
                statement: "The payment processor records the refund.",
                requiredOracle: "oracle:payment-processor",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:payment-record-unavailable",
            },
            {
                id: "criterion:customer-notification",
                statement: "The customer was notified through the approved channel.",
                requiredOracle: "oracle:notification-system",
                absentFromTrace: true,
                canBeIndependentOracle: true,
                namedGapIfUnavailable: "gap:customer-notification-unavailable",
            },
        ],
    },
]);
function inferTraceKind(text) {
    const lower = text.toLowerCase();
    if (/\b(refund|stripe|charge|payment)\b/.test(lower))
        return "refund";
    if (/\b(deploy(?:ed)?|release(?:d)?|rollout|hosted)\b/.test(lower))
        return "deploy";
    if (/\b(pr|pull request|merge|merged)\b/.test(lower))
        return "pr_merge";
    if (/\b(ticket|issue|jira|linear|close[sd]?)\b/.test(lower))
        return "ticket_closure";
    if (/\b(research|sources|cite|citation|survey)\b/.test(lower))
        return "research";
    if (/\b(code|patch|diff|test|build|commit)\b/.test(lower))
        return "code_change";
    return "ambiguous";
}
function digestProposal(input, sha256) {
    return canonicalSha256(input, sha256);
}
export function normalizeCompletionProposalTrace(input) {
    const events = input.events
        .map((event) => completionProposalTraceEventSchema.parse(event))
        .sort((left, right) => left.id.localeCompare(right.id));
    const usableText = events
        .filter((event) => event.permission === "content")
        .map((event) => event.text)
        .join("\n")
        .trim();
    const observationBoundary = events
        .filter((event) => event.trusted && event.independentOracle !== null)
        .map((event) => event.independentOracle)
        .sort();
    const availableOracles = [...new Set(observationBoundary)].sort();
    const withoutDigest = {
        traceText: usableText || "ambiguous trace",
        observationBoundary,
        availableOracles,
        sourceEventDigests: Object.fromEntries(events.map((event) => [event.id, event.digest])),
        withheldEventIds: events
            .filter((event) => event.permission === "withheld")
            .map((event) => event.id),
        redactedEventIds: events
            .filter((event) => event.permission === "redacted")
            .map((event) => event.id),
        untrustedEventIds: events.filter((event) => !event.trusted).map((event) => event.id),
    };
    return completionProposalTraceInputSchema.parse({
        ...withoutDigest,
        traceDigest: canonicalSha256(withoutDigest, input.sha256),
    });
}
export function createCompletionProposal(input) {
    const templates = (input.templates ?? DEFAULT_COMPLETION_TEMPLATES).map((template) => completionTemplateSchema.parse(template));
    const traceKind = inferTraceKind(input.traceText);
    const sourceTraceDigest = canonicalSha256({ traceText: input.traceText, observationBoundary: [...input.observationBoundary].sort() }, input.sha256);
    if (traceKind === "ambiguous") {
        const withoutDigest = {
            protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
            proposalId: input.proposalId,
            traceKind,
            selectedTemplateId: null,
            state: "abstain",
            evidenceBackedScore: 0,
            rankedByLocalModel: input.localModelRanked ?? false,
            activatedContract: false,
            criteria: [],
            namedGaps: ["gap:ambiguous-trace"],
            sourceTraceDigest,
        };
        return completionProposalSchema.parse({
            ...withoutDigest,
            proposalDigest: digestProposal(withoutDigest, input.sha256),
        });
    }
    const matching = templates.filter((template) => template.traceKind === traceKind);
    const bounded = matching.slice(0, input.maxAlternatives ?? 3);
    const selected = bounded[0];
    if (!selected) {
        const withoutDigest = {
            protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
            proposalId: input.proposalId,
            traceKind,
            selectedTemplateId: null,
            state: "abstain",
            evidenceBackedScore: 0,
            rankedByLocalModel: input.localModelRanked ?? false,
            activatedContract: false,
            criteria: [],
            namedGaps: ["gap:no-template"],
            sourceTraceDigest,
        };
        return completionProposalSchema.parse({
            ...withoutDigest,
            proposalDigest: digestProposal(withoutDigest, input.sha256),
        });
    }
    const observation = new Set(input.observationBoundary);
    const availableOracles = new Set(input.availableOracles);
    const criteria = selected.criteria.map((criterion) => {
        const oracleObserved = observation.has(criterion.requiredOracle);
        const oracleAvailable = availableOracles.has(criterion.requiredOracle);
        return completionProposalCriterionSchema.parse({
            ...criterion,
            canBeIndependentOracle: criterion.canBeIndependentOracle && oracleAvailable,
            absentFromTrace: criterion.absentFromTrace || !oracleObserved,
        });
    });
    const namedGaps = criteria
        .filter((criterion) => !availableOracles.has(criterion.requiredOracle) || criterion.absentFromTrace)
        .map((criterion) => criterion.namedGapIfUnavailable)
        .sort();
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        proposalId: input.proposalId,
        traceKind,
        selectedTemplateId: selected.id,
        state: criteria.every((criterion) => criterion.canBeIndependentOracle) ? "proposed" : "abstain",
        evidenceBackedScore: criteria.filter((criterion) => availableOracles.has(criterion.requiredOracle)).length /
            criteria.length,
        rankedByLocalModel: input.localModelRanked ?? false,
        activatedContract: false,
        criteria,
        namedGaps,
        sourceTraceDigest,
    };
    return completionProposalSchema.parse({
        ...withoutDigest,
        proposalDigest: digestProposal(withoutDigest, input.sha256),
    });
}
